import React, { useCallback, useEffect, useState } from "react";
import { CFormSelect } from "@coreui/react";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { CsrAPI } from "../apis/CsrAPI";
import FormatDate from "../utils/FormatDate";

// Only use Sentry in production environment
const isProduction = process.env.NODE_ENV === "production";

// The versions payload is a `requestId -> ISO timestamp` map, but it arrives
// wrapped in an envelope, so unwrap before reading request ids off the keys —
// otherwise the envelope key itself gets mistaken for a request id.
const VERSION_ENVELOPE_KEYS = ["versions", "renders", "items", "data"];
const REQUEST_ID_KEYS = ["requestId", "request_id", "id"];
const TIMESTAMP_KEYS = [
  "renderedAt",
  "rendered_at",
  "renderTime",
  "render_time",
  "finishedAt",
  "finished_at",
  "createdAt",
  "created_at",
  "timestamp",
];

const firstValue = (object, keys) => {
  const key = keys.find(candidate => object[candidate] != null);
  return key ? object[key] : undefined;
};

const unwrapVersions = payload => {
  let current = payload;
  // A couple of passes is plenty for any nesting the backend uses; the loop
  // stops as soon as no envelope key is left.
  for (
    let depth = 0;
    depth < 3 &&
    current &&
    typeof current === "object" &&
    !Array.isArray(current);
    depth += 1
  ) {
    const envelopeKey = VERSION_ENVELOPE_KEYS.find(
      key => current[key] && typeof current[key] === "object"
    );
    if (!envelopeKey) break;
    current = current[envelopeKey];
  }
  return current;
};

// Flattens the payload into `{ requestId, renderedAt }` pairs. Both shapes the
// map can take are accepted: a timestamp string per request id, or an object
// per render carrying its own id and time.
const toVersionEntries = payload => {
  const source = unwrapVersions(payload);

  if (Array.isArray(source)) {
    return source
      .map(item =>
        // A bare id in the list means the timestamp isn't on offer.
        item && typeof item === "object"
          ? {
              requestId: firstValue(item, REQUEST_ID_KEYS),
              renderedAt: firstValue(item, TIMESTAMP_KEYS),
            }
          : { requestId: item, renderedAt: undefined }
      )
      .filter(entry => entry.requestId != null)
      .map(entry => ({ ...entry, requestId: String(entry.requestId) }));
  }

  if (!source || typeof source !== "object") return [];

  return Object.entries(source).map(([requestId, value]) => ({
    requestId: String(requestId),
    renderedAt:
      value && typeof value === "object"
        ? firstValue(value, TIMESTAMP_KEYS)
        : value,
  }));
};

const parseRenderedAt = renderedAt => {
  const time = new Date(renderedAt).getTime();
  return Number.isNaN(time) ? null : time;
};

// Numbering follows generation order (oldest render is Version 1) while the
// list itself is shown newest first. Renders with an unreadable timestamp keep
// their place rather than dropping out of the picker.
const toVersionOptions = payload =>
  toVersionEntries(payload)
    .sort(
      (a, b) =>
        (parseRenderedAt(a.renderedAt) ?? 0) -
        (parseRenderedAt(b.renderedAt) ?? 0)
    )
    .map((version, index) => ({ ...version, versionNumber: index + 1 }))
    .reverse();

const versionLabel = version => {
  const time = parseRenderedAt(version.renderedAt);
  return time === null
    ? `Version ${version.versionNumber}`
    : `Version ${version.versionNumber} - ${FormatDate(version.renderedAt)}`;
};

// History picker for the SCS file page: every finished render of a summary,
// each one downloadable as its generated .docx plus its .xlsx traffic-light
// report.
const ScsRenderHistoryDropdown = ({
  summaryId,
  fileBaseName = "SCS",
  // Bumped by the page whenever a render completes, so a freshly generated
  // version shows up without a reload.
  refreshToken = 0,
  disabled = false,
}) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRequestId, setSelectedRequestId] = useState("");

  const fetchVersions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const versionsMap = await CsrAPI.getSafetySummaryRenderVersions({
        summaryId,
      });
      setVersions(toVersionOptions(versionsMap));
    } catch (fetchError) {
      // 404 just means nothing has been rendered yet — an empty picker says
      // that clearly enough without an error message.
      if (fetchError.response?.status === 404) {
        setVersions([]);
        return;
      }

      if (isProduction) {
        Sentry.captureException(fetchError);
      }

      if (!fetchError?.response) {
        setError("No Server Response");
      } else if (fetchError.response.status === 401) {
        setError("Invalid user credentials");
      } else if (fetchError.response.status === 403) {
        setError("Access denied");
      } else {
        setError("Failed to load version history");
      }
    } finally {
      setLoading(false);
    }
  }, [summaryId]);

  useEffect(() => {
    if (summaryId) {
      fetchVersions();
    }
  }, [summaryId, fetchVersions, refreshToken]);

  const handleVersionSelect = async event => {
    const requestId = event.target.value;
    if (!requestId) return;

    const version = versions.find(item => item.requestId === requestId);
    setSelectedRequestId(requestId);
    setDownloading(true);

    try {
      const { documentUrl, reportUrl } =
        await CsrAPI.getSafetySummaryRenderVersion({ summaryId, requestId });

      const suffix = version ? `_v${version.versionNumber}` : "";
      // Two separate downloads — the generated document and its report.
      if (documentUrl) {
        const documentBlob = await CsrAPI.downloadPresignedFile({
          url: documentUrl,
        });
        saveAs(documentBlob, `${fileBaseName}${suffix}.docx`);
      }
      if (reportUrl) {
        const reportBlob = await CsrAPI.downloadPresignedFile({
          url: reportUrl,
        });
        saveAs(reportBlob, `${fileBaseName}${suffix}_report.xlsx`);
      }

      if (documentUrl || reportUrl) {
        toast.success("Version downloaded successfully!");
      } else {
        toast.error("No files are available for this version.");
      }
    } catch (downloadError) {
      if (isProduction) {
        Sentry.captureException(downloadError);
      }

      let errorMessage = "Failed to download version";
      if (!downloadError?.response) {
        errorMessage = "No Server Response";
      } else if (downloadError.response.status === 401) {
        errorMessage = "Invalid user credentials";
      } else if (downloadError.response.status === 403) {
        errorMessage = "Access denied";
      } else if (downloadError.response.status === 404) {
        errorMessage = "This version is no longer available";
      }

      toast.error(errorMessage);
    } finally {
      setDownloading(false);
      setSelectedRequestId(""); // Reset selection
    }
  };

  const busy = loading || downloading;

  return (
    <div className="traffic-light-reports-dropdown">
      <CFormSelect
        className="input-select traffic-light-reports-select"
        value={selectedRequestId}
        onChange={handleVersionSelect}
        disabled={disabled || busy || versions.length === 0}
        aria-label="History (previous SCS versions)">
        <option value="">
          {loading
            ? "Loading versions..."
            : downloading
              ? "Downloading..."
              : versions.length === 0
                ? "No reports available"
                : "Select version"}
        </option>
        {versions.map(version => (
          <option key={version.requestId} value={version.requestId}>
            {versionLabel(version)}
          </option>
        ))}
      </CFormSelect>
      {error && (
        <div
          className="error-message"
          style={{
            color: "#dc3545",
            fontSize: "12px",
            marginTop: "4px",
          }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default ScsRenderHistoryDropdown;
